import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Drawer, Form, Input, Select, Switch,
  Space, Tag, Popconfirm, message, Typography,
  InputNumber,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  ProductType, FieldDef,
  getProductTypeCatalog, createProductType, updateProductType, deleteProductType,
} from '../api/productTypeCatalog';

const { Title } = Typography;

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Текст' },
  { value: 'select', label: 'Выбор из списка' },
  { value: 'tags', label: 'Теги (множественный выбор)' },
  { value: 'number', label: 'Число' },
];

const emptyField = (): FieldDef => ({
  key: '',
  label: '',
  field_type: 'text',
  required: false,
  options: [],
  placeholder: '',
});

const ProductTypesPage: React.FC = () => {
  const [types, setTypes] = useState<ProductType[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<ProductType | null>(null);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTypes(await getProductTypeCatalog());
    } catch {
      message.error('Ошибка загрузки типов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDrawer = (item: ProductType | null) => {
    setSelected(item);
    if (item) {
      form.setFieldsValue({
        name: item.name,
        sort_order: item.sort_order,
        is_active: item.is_active,
      });
      setFields(item.fields.map(f => ({ ...f })));
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true, sort_order: types.length });
      setFields([]);
    }
    setDrawerOpen(true);
  };

  const handleSave = async (values: { name: string; sort_order: number; is_active: boolean }) => {
    try {
      if (selected) {
        await updateProductType(selected.id, { ...values, fields });
        message.success('Тип обновлён');
      } else {
        await createProductType({ ...values, fields });
        message.success('Тип создан');
      }
      setDrawerOpen(false);
      load();
    } catch {
      message.error('Ошибка сохранения');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteProductType(selected.id);
      message.success('Тип удалён');
      setDrawerOpen(false);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const updateField = (idx: number, patch: Partial<FieldDef>) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  };

  const removeField = (idx: number) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
  };

  const columns: ColumnsType<ProductType> = [
    {
      title: '№',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 60,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Полей',
      key: 'fields',
      width: 80,
      render: (_, r) => r.fields.length,
    },
    {
      title: 'Активен',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (v: boolean) => v ? <Tag color="green">Да</Tag> : <Tag color="default">Нет</Tag>,
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Типы товаров</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer(null)}>
          Добавить тип
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={types}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={false}
        onRow={(record) => ({
          onClick: () => openDrawer(record),
          style: { cursor: 'pointer' },
        })}
      />

      <Drawer
        title={selected ? `Редактировать: ${selected.name}` : 'Новый тип'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={560}
        extra={
          <Space>
            {selected && (
              <Popconfirm
                title="Удалить тип?"
                description="Нельзя удалить если есть товары с этим типом"
                onConfirm={handleDelete}
                okText="Удалить"
                cancelText="Отмена"
              >
                <Button danger icon={<DeleteOutlined />}>Удалить</Button>
              </Popconfirm>
            )}
            <Button type="primary" onClick={() => form.submit()}>Сохранить</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="name" label="Название типа" rules={[{ required: true }]}>
            <Input placeholder="масло моторное" />
          </Form.Item>
          <Space>
            <Form.Item name="sort_order" label="Порядок">
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item name="is_active" label="Активен" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
        </Form>

        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong>Поля ({fields.length})</strong>
            <Button size="small" icon={<PlusOutlined />} onClick={() => setFields(prev => [...prev, emptyField()])}>
              Добавить поле
            </Button>
          </div>

          {fields.map((field, idx) => (
            <div key={idx} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong style={{ color: '#666', fontSize: 12 }}>Поле #{idx + 1}</strong>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeField(idx)} />
              </div>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                <Space>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Ключ (key)</div>
                    <Input
                      size="small"
                      value={field.key}
                      placeholder="viscosity"
                      style={{ width: 150 }}
                      onChange={e => updateField(idx, { key: e.target.value })}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Метка (label)</div>
                    <Input
                      size="small"
                      value={field.label}
                      placeholder="Вязкость"
                      style={{ width: 150 }}
                      onChange={e => updateField(idx, { label: e.target.value })}
                    />
                  </div>
                </Space>
                <Space>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Тип поля</div>
                    <Select
                      size="small"
                      value={field.field_type}
                      options={FIELD_TYPE_OPTIONS}
                      style={{ width: 180 }}
                      onChange={v => updateField(idx, { field_type: v })}
                    />
                  </div>
                  <div style={{ paddingTop: 18 }}>
                    <Switch
                      size="small"
                      checked={field.required}
                      onChange={v => updateField(idx, { required: v })}
                    />
                    <span style={{ marginLeft: 6, fontSize: 12 }}>Обязательное</span>
                  </div>
                </Space>
                <div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Подсказка (placeholder)</div>
                  <Input
                    size="small"
                    value={field.placeholder}
                    onChange={e => updateField(idx, { placeholder: e.target.value })}
                  />
                </div>
                {field.field_type === 'select' && (
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Варианты (через Enter)</div>
                    <Select
                      size="small"
                      mode="tags"
                      value={field.options}
                      style={{ width: '100%' }}
                      onChange={v => updateField(idx, { options: v })}
                      placeholder="Введите вариант и нажмите Enter"
                    />
                  </div>
                )}
              </Space>
            </div>
          ))}
        </div>
      </Drawer>
    </>
  );
};

export default ProductTypesPage;
